import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_ACCOUNT } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputPassword from "@/design/forms/InputPassword";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import StringForm from "@/design/forms/StringForm";
import { showSnackbar } from "@/store/reducers/app";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Sesame from "@/utils/Sesame";
import { getAccessLevel, toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viChainSlash,
  viCircleCheck,
  viPassword,
  viPerson,
  viShieldKeyhole,
  viSpinnerThird,
  viXmark,
} from "@sippy-platform/valkyrie";

import { Button, Container, Grid, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";

import ChangeEmail from "../../backstage/accounts/ChangeEmail";
import accountReducer from "./accountReducer";

function Account() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(() => id === "new");
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setChangePassword(id === "new");
    setShowPassword(false);
  }

  const auth = useSelector((state) => state.auth);
  const pbx = useSelector((state) => state.domain.pbx);

  const {
    value: account,
    dispatch: accountDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_ACCOUNT,
    reducer: accountReducer,
    validator: () => ({
      firstname: string().required({ message: "First name is required." }).length([1, 40], {
        message: "First name must be at most 40 characters.",
      }),
      lastName: string().length([1, 40], {
        message: "Last name must be at most 40 characters.",
      }),
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "Email address isn't a valid format." }),
      password: string()
        .required({
          message: "Password is required.",
          condition: !account.id || changePassword,
        })
        .length([8], {
          message: "Password must be at least 8 characters.",
          condition: !account.id || changePassword,
        })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password should have at least one letter and one number.",
        })
        .is(account.confirmPassword, {
          message: "Passwords do not match.",
          condition: !account.id || changePassword,
        }),
      accessLevel: number().required({
        message: "You must give the user a role.",
        condition: !account.id,
      }),
    }),
    onSubmit: () => Api.storePbxAccount(account, pbx),
    onSuccess: (response) => {
      if (!account.id) {
        accountDispatch({ type: "id", value: response.data.account.id });
        navigate(`../${response.data.account.id}`);
      }

      Promise.resolve(Api.getAccounts());
    },
  });

  // Get general data
  useEffect(() => {
    let ignore = false;

    if (Number(id) === account.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      accountDispatch({
        type: "set",
        value: {
          ...NEW_ACCOUNT,
          pbxId: pbx.context,
          accessLevel: null,
        },
      });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getAccount({ id: Number(id) }, false))
      .then((response) => {
        if (!ignore) {
          setChangePassword(false);
          accountDispatch({ type: "set", value: response.data.account });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [account.id, accountDispatch, id, pbx.context]);

  // Handle changes
  const formHandler = (property, value) => {
    accountDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Page specific functions
  const generatePassword = () => {
    const nextPassword = new Sesame(16).generate();
    accountDispatch({ type: "resetPassword", value: nextPassword });
    setShowPassword(true);
    setSaveState(SaveState.UNSAVED);
  };

  // Direct API actions
  function deleteAccountFromPbx(account) {
    if (pbx.id) {
      Promise.resolve(Api.deleteAccountFromPbx(account, pbx)).then(() => {
        Promise.resolve(Api.getAccounts());
        navigate(`/pbx/${pbx.context}/my-pbx/pbx`);
      });
    }
  }

  const [pendingSendReset, setPendingSendReset] = useState(false);
  function sendResetLink() {
    setPendingSendReset(true);

    Promise.resolve(Api.postPasswordForgotten(account))
      .then(() => dispatch(showSnackbar("Password reset link send to user.")))
      .finally(() => setPendingSendReset(false));
  }

  const [pendingResendConfirmation, setPendingResendConfirmation] = useState(false);
  function resendConfirmationLink() {
    setPendingResendConfirmation(true);

    Promise.resolve(Api.postResendEmailConfirmation(account))
      .then(() => dispatch(showSnackbar("Account confirmation link send to user.")))
      .finally(() => setPendingResendConfirmation(false));
  }

  // Other functions
  function cancelChangePassword() {
    setChangePassword(false);
    accountDispatch({ type: "resetPassword", value: undefined });
    setShowPassword(false);
  }
  const accountName = useMemo(() => {
    return account.firstname || account.lastname
      ? `${account.firstname ?? ""} ${account.lastname ?? ""}`.trim()
      : "Unnamed account";
  }, [account.firstname, account.lastname]);

  return (
    <Container>
      <title>{`${accountName} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={"/my-pbx"}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={accountName} />
              </IconButton>
            ) : (
              <Avatar text={accountName} />
            )
          }
          action={<SaveIndicator formState={formState} errors={allErrors} save={save} />}
          title={accountName}
          subheader={account.email || ""}
        />
        <FormGroup
          title="Identity"
          actions={(auth.accessLevel >= 2 || account.id === auth.id) && <ChangeEmail account={account} />}
        >
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="firstname"
              label="First name"
              onChange={formHandler}
              value={account.firstname}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText id="lastname" label="Last name" onChange={formHandler} value={account.lastname} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="email"
              label="Email address"
              onChange={formHandler}
              value={account.email}
              error={errors}
              disabled={auth.accessLevel >= 2 || account.id === auth.id}
            />
          </Grid>
        </FormGroup>
        {!account.confirmed && (
          <FormGroup
            title="Confirm account"
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => resendConfirmationLink()}
                  startIcon={
                    <Valkyrie
                      icon={pendingResendConfirmation ? viSpinnerThird : viCircleCheck}
                      spin={pendingResendConfirmation}
                    />
                  }
                  disabled={pendingResendConfirmation}
                >
                  Resend confirmation
                </Button>
              </Stack>
            }
          />
        )}
        <FormGroup
          title="Password"
          actions={
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              {account?.id && (
                <Button
                  onClick={sendResetLink}
                  startIcon={
                    <Valkyrie icon={pendingSendReset ? viSpinnerThird : viShieldKeyhole} spin={pendingSendReset} />
                  }
                  disabled={pendingSendReset}
                >
                  Send password reset
                </Button>
              )}
              {changePassword ? (
                <Button
                  variant="contained"
                  onClick={cancelChangePassword}
                  startIcon={<Valkyrie icon={account.id ? viXmark : viPerson} />}
                >
                  {account.id ? "Cancel" : "Let user set password"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setChangePassword(true)}
                  startIcon={<Valkyrie icon={viPassword} />}
                >
                  {account.id ? "Change password" : "Set password"}
                </Button>
              )}
            </Stack>
          }
        >
          {changePassword ? (
            <>
              <Grid size={12}>
                <Button
                  variant="outlined"
                  onClick={() => generatePassword()}
                  startIcon={<Valkyrie icon={viShieldKeyhole} />}
                >
                  Generate password
                </Button>
              </Grid>
              <Grid size={12}>
                <Typography variant="body2">
                  The new password cannot be seen after you have left this page. The user won't be notified of the
                  password. Make sure to properly and securely communicate the password with the user. A password has to
                  have at least 8 characters with a combination of letters and numbers.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputPassword
                  required
                  showPasswordStrength
                  showPassword={showPassword}
                  id="password"
                  label="Password"
                  onChange={formHandler}
                  value={account.password}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputPassword
                  required
                  id="confirmPassword"
                  label="Confirm password"
                  onChange={formHandler}
                  value={account.confirmPassword}
                  error={errors}
                />
              </Grid>
            </>
          ) : (
            !account?.id && (
              <Grid size={12}>
                <Typography>The user will set their own password when validating their account.</Typography>
              </Grid>
            )
          )}
        </FormGroup>
        {!account.id && (
          <FormGroup title="Permissions">
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputSelect
                id="accessLevel"
                label="Role"
                onChange={formHandler}
                value={account.accessLevel}
                selectValue="value"
                selects={[
                  { value: 2, title: "Customer" },
                  { value: 4, title: "Viewer" },
                ]}
                getOptionLabel={(option) => option.title}
                helperText="The role of an account cannot be changed after saving it."
                error={errors}
              />
            </Grid>
          </FormGroup>
        )}
        {account.id && auth.accessLevel <= 1 && (
          <FormGroup
            title="PBX"
            actions={
              <Button
                variant="contained"
                onClick={() => deleteAccountFromPbx(account)}
                startIcon={<Valkyrie icon={viChainSlash} />}
              >
                Disconnect account
              </Button>
            }
          />
        )}
        {account.id && (
          <FormGroup title="Details">
            {account.createdDate && account.createdDate !== "0000-00-00" && (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Joined" text={format(parseISO(account.createdDate), "d MMMM yyyy")} />
              </Grid>
            )}
            {account.updatedDate && account.updatedDate !== "0000-00-00" && (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Last changed" text={format(parseISO(account.updatedDate), "d MMMM yyyy")} />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <StringForm label="Type" text={getAccessLevel(account.accessLevel)} />
            </Grid>
          </FormGroup>
        )}
      </Stack>
    </Container>
  );
}

export default Account;
